<script setup>
import { computed } from 'vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import PermissionTooltip from '@/components/Moleculas/PermissionTooltip.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import RemoveMenuItem from '@/components/ui/RemoveMenuItem.vue'
import Spinner from '@/components/ui/Spinner.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconInfo from '@/components/icon/IconInfo.vue'
import IconLiveBroadcast from '@/components/icon/IconLiveBroadcast.vue'
import IconStackedLayers from '@/components/icon/IconStackedLayers.vue'
import { usePlatformListContext } from './platformListContext'
import { getCountryFlag, regionName, pushStreamFailOverEnabled, getLinkedSourceTooltip } from '@/Utils/platformStatus'

const props = defineProps({
	platform: {
		type: Object,
		required: true,
	},
	kind: {
		type: String,
		default: 'stream',
		validator: (v) => ['stream', 'transcoder'].includes(v),
	},
})

const ctx = usePlatformListContext()

const displayName = computed(() => {
	const p = props.platform
	if (props.kind === 'transcoder') {
		return p.castrName || p.streamObject?.name || p.name
	}
	return p.streamObject ? p.streamObject.name : p.name
})

const region = computed(() => regionName(props.platform))
const showRegion = computed(() => {
	if (props.kind === 'stream') return true
	const p = props.platform
	return !!(p.streamObject && (p.streamObject.uiRegion || p.streamObject.region)) || !!p.region
})

const rowClass = computed(() => [
	ctx.streamType,
	ctx.streamType === 'scheduled' ? 'p-3 bg-surface-3 rounded-lg' : 'p-3 rounded-lg hover:bg-surface-2',
])

const streamEnabled = computed(() => props.platform.streamObject?.enabled)
const linkedTooltip = computed(() => getLinkedSourceTooltip(props.platform))
const failOver = computed(() => pushStreamFailOverEnabled(props.platform))
const switchDisabled = computed(() =>
	ctx.cannotTogglePlatforms
	|| props.platform.tooltipId !== undefined
	|| failOver.value
	|| (props.kind === 'stream' && props.platform.linkedSource)
)
</script>

<template>
	<div
		class="platform-item w-full min-w-0 flex items-center justify-between gap-x-3"
		:class="rowClass"
	>
		<div class="flex items-center gap-3 min-w-0 grow">
			<div class="shrink-0 size-10 rounded-lg bg-surface-3 flex items-center justify-center text-surface-7">
				<icon-base width="20" height="20" :widthViewBox="24" :heightViewBox="24" fill="none">
					<icon-live-broadcast v-if="kind === 'stream'" />
					<icon-stacked-layers v-else />
				</icon-base>
			</div>
			<div class="flex flex-col min-w-0 md:flex-1">
				<h4 class="text-base font-semibold w-full truncate min-w-0 max-w-full mb-1">{{ displayName }}</h4>
				<span v-if="showRegion && region" class="platform-item__server flex items-center text-dark-9 text-tiny truncate">
					<img :src="getCountryFlag(region, ctx.regions)" class="region-flag mr-2" width="18">
					{{ region }}
				</span>
			</div>
		</div>
		<div class="flex items-center gap-x-2 shrink-0" :id="`${platform.tooltipId}`">
			<Spinner
				v-if="platform.removingProcessing"
				text=""
				classes="text-dark-8"
				spinner-color="var(--c-dark-8)"
				spinner-size="15px"
			/>
			<Tooltip
				v-if="platform.connected && streamEnabled && ((ctx.mediaPulse && !ctx.mediaPulse.alive) || (ctx.mediaPulse && ctx.mediaPulse.alive && !platform.enabled))"
				content="This stream is currently live. Adding a live stream may cause issues — add it while it's offline."
			>
				<icon-base class="text-danger" fill="none">
					<icon-info />
				</icon-base>
			</Tooltip>
			<StatusPill v-if="platform.enabled && platform.connected" variant="success" pulse pill>Live</StatusPill>
			<Tooltip v-if="!platform.enabled && platform.streamObject && !platform.streamObject.enabled" content="You can't stream to a disabled stream, please enable this stream first.">
				<icon-base class="text-danger" fill="none">
					<icon-info />
				</icon-base>
			</Tooltip>
			<StatusPill v-if="!platform.connected && platform.streamObject" variant="muted" pill>
				{{ platform.streamObject.enabled ? 'Offline' : 'Disabled' }}
			</StatusPill>
			<PermissionTooltip v-if="ctx.cannotTogglePlatforms" :email="ctx.userEmail">
				<FormSwitch disabled />
			</PermissionTooltip>
			<Tooltip v-else-if="failOver" content="This stream has back-up enabled. Please disable back-up for this to work.">
				<FormSwitch disabled />
			</Tooltip>
			<Tooltip
				v-else
				:content="platform.tooltipId ? 'Infinite loop mode does not support Facebook, please use Schedule Date time mode instead.' : linkedTooltip"
				:active="linkedTooltip.length > 0 || platform.tooltipId !== undefined"
			>
				<FormSwitch
					:id="'platform-toggle' + platform._id"
					label-position="left"
					label-styles="text-sm text-surface-9"
					:disabled="switchDisabled"
					v-model="platform.enabled"
					@change="ctx.onTogglePlatformStatus(platform)"
				/>
			</Tooltip>
			<Tooltip :content="linkedTooltip" :active="kind === 'stream' && linkedTooltip.length > 0">
				<DropDownMenu isIcon no-border type="secondary" position="right" :disabled="ctx.deleteBlock || (kind === 'stream' && platform.linkedSource)">
					<template #toggle-button>
						<img src="@/assets/icons/settings.svg" alt="Settings">
					</template>
					<template #menu-items>
						<RemoveMenuItem @confirm="ctx.onInlineRemove(platform)" />
					</template>
				</DropDownMenu>
			</Tooltip>
		</div>
	</div>
</template>
